// scroll-utils.js

/* ─── Reduced motion ─────────────────────────────────────────────────────── */

// When the user prefers reduced motion, scroll handlers are not bound — only
// an initial update runs so elements start at their correct position.
// Pair this with @media (prefers-reduced-motion: reduce) in your SCSS to reset
// any transforms/clip-paths to their resting state.
const _reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ─── Debug ──────────────────────────────────────────────────────────────── */

// Add data-scroll-debug to <html> to enable the debug panel.
const _isDebug  = () => document.documentElement.hasAttribute('data-scroll-debug');
let   _debugPanel = null;

function _updateDebugPanel() {
  if (!_isDebug()) return;

  if (!_debugPanel) {
    _debugPanel = document.createElement('div');
    _debugPanel.id = '_scroll-bind-debug';
    _debugPanel.style.cssText = [
      'position:fixed;bottom:0;left:0;max-height:40vh;overflow-y:auto',
      'background:rgba(0,0,0,.88);color:#0f0;font:11px/1.6 monospace',
      'padding:8px 12px;z-index:99999;pointer-events:none;min-width:300px',
    ].join(';');
    document.body.appendChild(_debugPanel);
  }

  const rows = _cachedVarElements.flatMap(({ el, configs, currentVals }) =>
    configs.map(cfg => {
      const id  = el.id ? `#${el.id}` : el.className.split(' ').find(Boolean) ?? 'el';
      const val = (currentVals[cfg.var] ?? 0).toFixed(3);
      return `<div><span style="color:#888">${id}</span> <span style="color:#aaa">${cfg.var}</span> <b>${val}</b></div>`;
    })
  );

  _debugPanel.innerHTML = `<div style="color:#ff0;margin-bottom:4px">[scroll-bind]</div>${rows.join('')}`;
}

/* ─── Page-level scroll progress indicators ─────────────────────────────── */

let _indicatorLenis  = null;
let _indicatorScroll = null;
let _indicatorResize = null;

/**
 * Tracks page-level scroll position as a percentage-width on matching elements.
 * @param {string} selector      CSS selector for progress bar elements.
 * @param {object} lenisInstance
 */
export function initializeScrollProgressIndicators(selector, lenisInstance) {
  if (!lenisInstance) {
    console.error('[scroll-utils] Lenis instance required for scroll progress indicators.');
    return;
  }

  destroyScrollProgressIndicators();

  const indicators = document.querySelectorAll(selector);
  if (!indicators.length) return;

  const update = () => {
    const pct = lenisInstance.limit > 0
      ? Math.max(0, Math.min(100, (lenisInstance.scroll / lenisInstance.limit) * 100))
      : 0;
    indicators.forEach(el => { el.style.width = `${pct}%`; });
  };

  _indicatorLenis  = lenisInstance;
  _indicatorScroll = update;
  _indicatorResize = update;

  lenisInstance.on('scroll', _indicatorScroll);
  window.addEventListener('resize', _indicatorResize);
  update();
}

export function destroyScrollProgressIndicators() {
  if (_indicatorLenis && _indicatorScroll) _indicatorLenis.off('scroll', _indicatorScroll);
  if (_indicatorResize) window.removeEventListener('resize', _indicatorResize);
  _indicatorLenis  = null;
  _indicatorScroll = null;
  _indicatorResize = null;
}

/* ─── Shared anchor parser ───────────────────────────────────────────────── */

/**
 * Parse an anchor string: "<element-edge> <viewport-position>"
 *   element-edge      : top | center | bottom          (default: top)
 *   viewport-position : top | center | bottom | 0–100  (default: 0)
 *
 * @param {string} str  e.g. "top bottom", "center 50", "bottom 0"
 * @returns {{ elemFrac: number, vpFrac: number }}
 */
function parseAnchor(str) {
  const parts    = (str || '').trim().split(/\s+/);
  const elemPart = parts[0] || 'top';
  const vpPart   = parts[1] || '0';

  const elemFrac =
    elemPart === 'bottom' ? 1 :
    elemPart === 'center' ? 0.5 : 0;

  const vpFrac =
    vpPart === 'bottom' ? 1 :
    vpPart === 'center' ? 0.5 :
    vpPart === 'top'    ? 0 :
    parseFloat(vpPart) / 100;

  return { elemFrac, vpFrac };
}

/**
 * Returns a 0–1 progress value for a given rect and anchor pair.
 * scrollY cancels out when offsetTop = rect.top + scrollY, so rect alone is sufficient.
 *
 * @param {DOMRect} rect
 * @param {number}  windowHeight
 * @param {string}  startAnchor
 * @param {string}  endAnchor
 * @returns {number} 0–1
 */
function computeProgress(rect, windowHeight, startAnchor, endAnchor) {
  const { elemFrac: es, vpFrac: vs } = parseAnchor(startAnchor);
  const { elemFrac: ee, vpFrac: ve } = parseAnchor(endAnchor);

  const numerator   = windowHeight * vs - rect.top - es * rect.height;
  const denominator = (ee - es) * rect.height + windowHeight * (vs - ve);

  if (denominator === 0) return 0;
  return Math.max(0, Math.min(1, numerator / denominator));
}

/* ─── Element-level scroll percent  (data-scroll-percent) ───────────────────
 *
 *   data-scroll-percent            marker — no value needed
 *   data-scroll-start="top bottom" anchor where progress = 0   (default)
 *   data-scroll-end="top 0"        anchor where progress = 100 (default)
 *
 *   Sets --scroll-percent (0–100) on the element each scroll frame.
 *   CSS usage: opacity: calc(var(--scroll-percent) / 100);
 *
 * ─────────────────────────────────────────────────────────────────────────── */

let _elemLenis         = null;
let _elemScrollHandler = null;
let _elemResizeHandler = null;
let _cachedElements    = [];

function _runPercentUpdate(els) {
  if (!els.length) return;
  const wh = window.innerHeight;
  els.forEach(el => {
    const progress = computeProgress(
      el.getBoundingClientRect(), wh,
      el.dataset.scrollStart ?? 'top bottom',
      el.dataset.scrollEnd   ?? 'top 0'
    );
    el.style.setProperty('--scroll-percent', (progress * 100).toFixed(2));
  });
}

/**
 * Initialise scroll-percent tracking for all [data-scroll-percent] elements.
 * @param {object} lenisInstance
 */
export function initScrollPercentElements(lenisInstance) {
  if (!lenisInstance) {
    console.error('[scroll-utils] Lenis instance required for initScrollPercentElements.');
    return;
  }

  destroyScrollPercentElements();
  _elemLenis = lenisInstance;

  _cachedElements    = Array.from(document.querySelectorAll('[data-scroll-percent]'));
  _elemScrollHandler = () => _runPercentUpdate(_cachedElements);
  _elemResizeHandler = () => {
    _cachedElements = Array.from(document.querySelectorAll('[data-scroll-percent]'));
    _runPercentUpdate(_cachedElements);
  };

  window.addEventListener('resize', _elemResizeHandler);
  _runPercentUpdate(_cachedElements);

  if (!_reducedMotion.matches) {
    lenisInstance.on('scroll', _elemScrollHandler);
  }
}

/**
 * Re-query [data-scroll-percent] elements and push an immediate update.
 * Call after dynamic DOM insertion (ALM, etc.).
 */
export function refreshScrollPercentElements() {
  _cachedElements = Array.from(document.querySelectorAll('[data-scroll-percent]'));
  _runPercentUpdate(_cachedElements);
}

export function destroyScrollPercentElements() {
  if (_elemLenis && _elemScrollHandler) _elemLenis.off('scroll', _elemScrollHandler);
  if (_elemResizeHandler) window.removeEventListener('resize', _elemResizeHandler);
  _elemLenis         = null;
  _elemScrollHandler = null;
  _elemResizeHandler = null;
  _cachedElements    = [];
}

/* ─── Multi-var scroll tracking  (data-scroll-bind) ─────────────────────────
 *
 *   data-scroll-bind='[
 *     { "var": "--ty-in",  "start": "top bottom", "end": "top 0" },
 *     { "var": "--ty-out", "start": "top 0",       "end": "bottom 0", "ease": 0.08 }
 *   ]'
 *
 *   Options per config object:
 *     "var"   (required) CSS custom property name to set (0–1)
 *     "start" anchor where progress = 0  (default: "top bottom")
 *     "end"   anchor where progress = 1  (default: "top 0")
 *     "ease"  lerp factor 0–1 — smooths the value toward the target each frame.
 *             Lower = slower/smoother. Omit for instant (no lerp).
 *             Note: lerp speed is frame-rate dependent; at 60fps 0.08 ≈ 0.5s settle.
 *
 *   data-scroll-origin=".selector"
 *     Resolves which element's rect drives the calculation. Use when the element
 *     receiving the vars has a CSS transform — computing from its own
 *     post-transform rect creates a feedback loop. Closest ancestor matching the
 *     selector is preferred; falls back to document.querySelector.
 *
 * ─────────────────────────────────────────────────────────────────────────── */

let _varLenis          = null;
let _varScrollHandler  = null;
let _varResizeHandler  = null;
let _cachedVarElements = []; // { el, originEl, configs, currentVals }[]

function _parseScrollBindConfig(el) {
  try {
    const cfg = JSON.parse(el.dataset.scrollBind);
    return Array.isArray(cfg) ? cfg : [];
  } catch {
    console.warn('[scroll-utils] Invalid JSON in data-scroll-bind on', el);
    return [];
  }
}

function _resolveOrigin(el) {
  const sel = el.dataset.scrollOrigin;
  if (!sel) return el;
  return el.closest(sel) || document.querySelector(sel) || el;
}

function _cacheVarElements() {
  // originEl is resolved once so per-frame updates never trigger selector lookups.
  // currentVals stores lerped values between frames; null = uninitialized (snap on first tick).
  _cachedVarElements = Array.from(document.querySelectorAll('[data-scroll-bind]'))
    .map(el => {
      const configs     = _parseScrollBindConfig(el);
      const currentVals = {};
      configs.forEach(cfg => { currentVals[cfg.var] = null; });
      return { el, originEl: _resolveOrigin(el), configs, currentVals };
    })
    .filter(({ configs }) => configs.length > 0);
}

function _runVarUpdate() {
  if (!_cachedVarElements.length) return;
  const wh = window.innerHeight;

  _cachedVarElements.forEach(({ el, originEl, configs, currentVals }) => {
    const rect = originEl.getBoundingClientRect();
    configs.forEach(cfg => {
      const target = computeProgress(rect, wh, cfg.start ?? 'top bottom', cfg.end ?? 'top 0');
      const prev   = currentVals[cfg.var];
      const value  = (cfg.ease && prev !== null) ? prev + (target - prev) * cfg.ease : target;
      currentVals[cfg.var] = value;
      el.style.setProperty(cfg.var, value.toFixed(4));
    });
  });

  _updateDebugPanel();
}

/**
 * Initialise multi-var scroll tracking for all [data-scroll-bind] elements.
 * @param {object} lenisInstance
 */
export function initScrollVarElements(lenisInstance) {
  if (!lenisInstance) {
    console.error('[scroll-utils] Lenis instance required for initScrollVarElements.');
    return;
  }

  destroyScrollVarElements();
  _varLenis = lenisInstance;

  _cacheVarElements();
  _varScrollHandler = _runVarUpdate;
  _varResizeHandler = () => { _cacheVarElements(); _runVarUpdate(); };

  window.addEventListener('resize', _varResizeHandler);
  _runVarUpdate();

  if (!_reducedMotion.matches) {
    lenisInstance.on('scroll', _varScrollHandler);
  }
}

/**
 * Re-query [data-scroll-bind] elements and push an immediate update.
 * Call after dynamic DOM insertion (ALM, etc.).
 */
export function refreshScrollVarElements() {
  _cacheVarElements();
  _runVarUpdate();
}

export function destroyScrollVarElements() {
  if (_varLenis && _varScrollHandler) _varLenis.off('scroll', _varScrollHandler);
  if (_varResizeHandler) window.removeEventListener('resize', _varResizeHandler);
  _varLenis          = null;
  _varScrollHandler  = null;
  _varResizeHandler  = null;
  _cachedVarElements = [];

  _debugPanel?.remove();
  _debugPanel = null;
}

/* ─── Convenience ────────────────────────────────────────────────────────── */

/**
 * Refresh all scroll-utils tracking systems after dynamic DOM insertion.
 * Equivalent to refreshScrollPercentElements() + refreshScrollVarElements().
 */
export function refreshScrollElements() {
  refreshScrollPercentElements();
  refreshScrollVarElements();
}
